import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'

import { TouchTarget } from '../Button'
import { Link } from '../Link'
import { colors } from './Badge.colors'

type BadgeProps = { color?: keyof typeof colors }

export const Badge = ({ color = 'gray', className, ...props }: BadgeProps & React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'inline-flex items-center gap-x-1.5 rounded-md px-1.5 py-0.5 text-sm/5 font-medium sm:text-xs/5 forced-colors:outline',
        colors[color],
      )}
    />
  )
}

type LinkProps = Omit<React.ComponentPropsWithoutRef<'a'>, 'className'> & {
  as?: React.ElementType
  to?: string
  href?: string
}

type BadgeButtonProps = BadgeProps & {
  className?: string
  children: React.ReactNode
} & (Omit<Headless.ButtonProps, 'as' | 'className'> | LinkProps)

export const BadgeButton = forwardRef(
  ({ color = 'gray', className, children, ...props }: BadgeButtonProps, ref: React.ForwardedRef<HTMLElement>) => {
    const classes = clsx(
      className,
      'group relative inline-flex rounded-md focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary-500',
    )

    return 'href' in props || 'to' in props ? (
      <Link
        {...(props as unknown as React.ComponentPropsWithoutRef<typeof Link>)}
        className={classes}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      >
        <TouchTarget>
          <Badge color={color}>{children}</Badge>
        </TouchTarget>
      </Link>
    ) : (
      <Headless.Button
        {...(props as unknown as Omit<Headless.ButtonProps, 'as' | 'className'>)}
        className={classes}
        ref={ref}
      >
        <TouchTarget>
          <Badge color={color}>{children}</Badge>
        </TouchTarget>
      </Headless.Button>
    )
  },
)
